import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useAgentSelectedBotStore, isAgentBotSelectionValid } from '@/store/agentSelectedBot.store';

/**
 * Agents must pick a workspace bot before using live chat / monitoring routes.
 * Redirects to Home and opens the global bot picker with a return URL.
 */
export default function AgentBotGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const selection = useAgentSelectedBotStore((s) => s.selection);
  const openPicker = useAgentSelectedBotStore((s) => s.openPicker);
  const setPendingReturnPath = useAgentSelectedBotStore((s) => s.setPendingReturnPath);

  const isAgent = user?.role?.toLowerCase() === 'agent';
  const allowed = !!(selection && isAgentBotSelectionValid(user, selection));
  const needsGate = isAgent && !allowed;
  const gateRedirected = useRef(false);

  useEffect(() => {
    if (!needsGate) {
      gateRedirected.current = false;
      return;
    }
    if (gateRedirected.current) return;
    gateRedirected.current = true;
    const returnTo = `${location.pathname}${location.search}`;
    setPendingReturnPath(returnTo);
    openPicker();
    navigate('/dashboard', { replace: true });
  }, [needsGate, location.pathname, location.search, navigate, openPicker, setPendingReturnPath]);

  if (!isAgent) {
    return <>{children}</>;
  }

  if (allowed) {
    return <>{children}</>;
  }

  return null;
}
